import { type MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Menu, MenuItem } from '@mui/material';
import { Grid } from '@mui/system';
import { isEmpty } from 'lodash';
import moment from 'moment';
import {
  type Scenario,
  SpreadsheetNote,
} from 'api';
import { fundOwnershipUrl } from 'common/config/urls';
import { WATERFALL_SLUG } from 'common/constants/allocations';
import { CAP_TABLE_CURRENCY_PAGE } from 'common/constants/currencyPageTypes';
import { CAP_TABLE_REFERENCE_TITLE, CAP_TABLE_REFERENCE_TYPE } from 'common/constants/documents';
import { WATERFALL_PAGE_KEY } from 'common/constants/notes';
import { useFormat } from 'common/hooks';
import { useStore } from 'common/store';
import { CapTableVersionWithCompanyId, ChildCompany, CompanyInfo } from 'common/types/company';
import { UseFormatValues } from 'common/types/format';
import { UseStoreValues } from 'common/types/store';
import { CustomCompactContainer, CustomContainer } from 'components';
import ConfirmationDelete from 'components/Dialogs/ConfirmationDelete';
import ConfirmationDialog from 'components/Dialogs/ConfirmationDialog';
import WidgetDrawer from 'components/Drawer/widget-drawer/WidgetDrawer';
import { WidgetDrawerApplicationContext } from 'components/Drawer/widget-drawer/WidgetDrawer.props';
import EmptyTableMessage from 'components/EmptyTableMessage';
import { REGULAR_UNIT } from 'components/FeaturedSpreadsheet/constants';
import LoadingIndicator from 'components/LoadingIndicator';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import WorkbookContext from 'components/ScalarSpreadsheet/utilities/WorkbookContext';
import {
  useSecondaryNavigationContributionRegistration,
  useTertiaryNavigationRegistration,
} from 'context/FirmBaseLayoutContext';
import { ScalarSpreadsheetConfigDataInterface } from 'models/interfaces/spreadsheet-types/core-types';
import { NeedsFundOwnershipFirst } from 'pages/Allocation/allocations/components';
import { getValidSecurities, useGenerateChildrenCompaniesCapTablesVersions } from 'pages/CapTable/cap-table/utilities';
import { DownloadWaterfall, WaterfallTables } from 'pages/Waterfall/components';
import WaterfallContext from 'pages/Waterfall/data/WaterfallContext/WaterfallContext';
import {
  CAP_TABLE_ALIAS,
  MEASUREMENT_DATE_ALIAS,
} from 'pages/Waterfall/SpreadsheetConfig/constants';
import {
  FundExchangeRateContext
} from 'pages/Waterfall/SpreadsheetConfig/FundExchangeRate/FundExchangeRateContext/FundExchangeRateContext';
import {
  useFundExchangeRateSheet
} from 'pages/Waterfall/SpreadsheetConfig/FundExchangeRate/useCreateFundExchangeRateSheet/useFundExchangeRateSheet';
import {
  useFundExchangeRate
} from 'pages/Waterfall/SpreadsheetConfig/FundExchangeRate/useFundExchangeRate/useFundExchangeRate';
import useFundInvestmentSheetConfig
  from 'pages/Waterfall/SpreadsheetConfig/FundInvestmentSheetConfig/useFundInvestmentSheetConfig/useFundInvestmentSheetConfig';
import {
  useWaterfallFundSummarySheetConfig
} from 'pages/Waterfall/SpreadsheetConfig/FundSummarySheetConfig/useFundSummarySheetConfig/useWaterfallFundSummarySheetConfig';
import useCreateInputSheet
  from 'pages/Waterfall/SpreadsheetConfig/InputSheetConfig/useCreateInputSheet/useCreateInputSheet';
import useWaterfallSheetConfig
  from 'pages/Waterfall/SpreadsheetConfig/WaterfallSheetConfig/useWaterfallSheetConfig/useWaterfallSheetConfig';
import { WaterfallDisplayProps } from 'pages/Waterfall/WaterfallDisplay/WaterfallDisplay.props';
import { useCompanyMeasurementDateFilter, useDocuments } from 'services/hooks';
import { useGetCompanyInfoById } from 'services/hooks/company';
import { useNotes } from 'services/hooks/notes';
import useSpreadsheetNotes from 'services/hooks/spreadsheetNotes/useSpreadsheetNotes';
import RelatedEntityWaterfallSheets from '../components/RelatedEntityWaterfallSheets/RelatedEntityWaterfallSheets';
import SaveWaterfallViewDialog from '../components/SaveWaterfallViewDialog';
import { useWaterfallComputedValues } from '../hooks/useWaterfallComputedValues/useWaterfallComputedValues';
import { useWaterfallData } from '../hooks/useWaterfallData/useWaterfallData';
import { useWaterfallDistribution } from '../hooks/useWaterfallDistribution/useWaterfallDistribution';
import { useWaterfallInputHandlers } from '../hooks/useWaterfallInputHandlers/useWaterfallInputHandlers';
import { useWaterfallIrr } from '../hooks/useWaterfallIrr/useWaterfallIrr';
import { useWaterfallUrlDefaults } from '../hooks/useWaterfallUrlDefaults/useWaterfallUrlDefaults';
import { useWaterfallUrlState } from '../hooks/useWaterfallUrlState/useWaterfallUrlState';
import useWaterfallViewNavItems, { DEFAULT_VIEW_VALUE } from '../hooks/useWaterfallViewNavItems';
import { useCreateWaterfallView, useDeleteWaterfallView, useUpdateWaterfallView, useWaterfallViews } from '../hooks/useWaterfallViews';
import useFirmTotalSheetConfig
  from '../SpreadsheetConfig/FirmInvestmentSheetConfig/UseFirmTotalSheetConfig/useFirmTotalSheetConfig';
import { useRelatedEntitiesWaterfalls } from '../utilities';

const WATERFALL_CONTAINER_CLASS = `${WATERFALL_SLUG}-container`;
const WATERFALL_INPUT_CONTAINER_CLASS = `${WATERFALL_SLUG}-input-container`;
const WATERFALL_SUMMARY_CONTAINER_CLASS = `${WATERFALL_SLUG}-summary-container`;
const WATERFALL_RELATED_ENTITY_CONTAINER_CLASS = `${WATERFALL_SLUG}-related-entity-container`;

const WaterfallDisplay = ({ isFirmPage }: WaterfallDisplayProps) => {
  // ============================================================
  // External hooks (documents, notes, store, etc.)
  // ============================================================
  const { filesToSave, setFilesToSave, addReferenceForExistingDocuments } = useDocuments();
  const { notes, setNotes, notesHasChanged, saveNotes, onAddNote, onUpdateNotes, onDeleteNote } = useNotes();
  const { spreadsheetNotes, setSpreadsheetNotes, saveSpreadsheetNotes, hasChanged: spreadsheetNoteHasChanged, handleOnDeleteNote: handleOnDeleteSpreadsheetNote } = useSpreadsheetNotes();
  const getCompanyInfoById = useGetCompanyInfoById();
  const { selectedMeasurementDate: selectedMeasurementDateFromRoute, MDDialogue, measurementDates } = useCompanyMeasurementDateFilter();
  const [{ companyInfo, firmInfo, fundList, companyList }] = useStore() as UseStoreValues;
  const { id: companyIdFromRoute, child_companies } = companyInfo as CompanyInfo;
  const firmSlug = firmInfo?.slug ?? '';
  const companySlug = companyInfo?.slug ?? '';

  // ============================================================
  // URL State (Source of Truth)
  // ============================================================
  const urlState = useWaterfallUrlState({
    isFirmPage: isFirmPage ?? false,
    companyList,
    capTableList: undefined, // Will be set after data loads
    companyMeasurementDates: undefined, // Will be set after data loads
  });

  // Determine effective company ID (from URL for firm page, from route for company page)
  const effectiveCompanyId = isFirmPage ? urlState.companyIdFromUrl : companyIdFromRoute;

  // ============================================================
  // Data Fetching (depends on URL state)
  // ============================================================
  const waterfallData = useWaterfallData({
    companyId: effectiveCompanyId,
    measurementDateValue: urlState.measurementDateValueFromUrl,
    captableId: urlState.captableIdFromUrl,
    selectedMeasurementDateFromRoute,
    isFirmPage: isFirmPage ?? false,
  });

  // Re-create URL state with loaded data for proper resolution
  const urlStateWithData = useWaterfallUrlState({
    isFirmPage: isFirmPage ?? false,
    companyList,
    capTableList: waterfallData.capTableList,
    companyMeasurementDates: waterfallData.companyMeasurementDates,
  });

  // ============================================================
  // Initialize URL Defaults
  // ============================================================
  const { isInitialized, isInitializing, reset: resetUrlDefaults } = useWaterfallUrlDefaults({
    urlState: urlStateWithData,
    isFirmPage: isFirmPage ?? false,
    mostRecentMeasurementDate: waterfallData.mostRecentMeasurementDate,
    primaryCaptable: waterfallData.primaryCaptable,
    companyMeasurementDates: waterfallData.companyMeasurementDates,
    capTableList: waterfallData.capTableList,
    isMeasurementDatesLoading: waterfallData.isMeasurementDatesLoading,
    isCapTableListLoading: waterfallData.isCapTableListLoading,
    companyIdFromRoute,
    selectedMeasurementDateFromRoute,
  });

  // ============================================================
  // Computed Values
  // ============================================================
  const computedValues = useWaterfallComputedValues({
    equityValue: urlStateWithData.equityValueFromUrl,
    enterpriseValue: urlStateWithData.enterpriseValueFromUrl,
    cashOverride: urlStateWithData.cashOverrideFromUrl,
    debtOverride: urlStateWithData.debtOverrideFromUrl,
    financials: waterfallData.financials,
  });

  // ============================================================
  // Derived values for compatibility with existing code
  // ============================================================
  const selectedCompanyId = effectiveCompanyId;
  // IMPORTANT: For firm pages, always use URL state as source of truth
  // For company pages, use resolvedMeasurementDate from route context
  const companySelectedMeasurementDate = isFirmPage 
    ? urlStateWithData.selectedMeasurementDate 
    : (waterfallData.resolvedMeasurementDate || urlStateWithData.selectedMeasurementDate);
  const selectedCaptable = urlStateWithData.captableIdFromUrl;

  const exitDate = urlStateWithData.exitDateFromUrl ?? null;
  const equityValue = urlStateWithData.equityValueFromUrl ?? null;
  const enterpriseValue = urlStateWithData.enterpriseValueFromUrl ?? null;
  const {captableDetail} = waterfallData;
  const {capTableList} = waterfallData;
  const {companyMeasurementDates} = waterfallData;

  const { calculatedEquityValue, effectiveCash, effectiveDebt, cashIsFromFinancials, debtIsFromFinancials } = computedValues;

  // ============================================================
  // Local State (UI-specific, not in URL)
  // ============================================================
  const [fundIrrMap, setFundIrrMap] = useState<{ [key: number]: number }>({});
  const [childrenCapTables, setChildrenCapTables] = useState<CapTableVersionWithCompanyId[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fundInvestmentsSheetsNamesRef = useRef<string[]>([]);
  const mainCapTableRef = useRef<number | null>(null);
  const mainFundOwnershipRef = useRef<typeof captableDetail extends { fund_ownership: infer T } ? T : null>(null);

  // ============================================================
  // Callbacks for saving
  // ============================================================
  const saveDrawer = useCallback(async () => {
    const saveTasks: Promise<unknown>[] = [];

    if (!isEmpty(filesToSave) && companySelectedMeasurementDate && selectedCaptable) {
      saveTasks.push(
        addReferenceForExistingDocuments(
          Number(companySelectedMeasurementDate.id),
          filesToSave,
          Number(selectedCaptable),
          CAP_TABLE_REFERENCE_TYPE
        )
      );
    }

    if (!isEmpty(notes) && notesHasChanged) {
      saveTasks.push(
        new Promise<void>((resolve, reject) => {
          saveNotes(undefined, {
            onSettled: resolve,
            onError: reject,
          });
        })
      );
    }

    if (!isEmpty(spreadsheetNotes) && spreadsheetNoteHasChanged) {
      saveTasks.push(saveSpreadsheetNotes());
    }

    if (saveTasks.length > 0) {
      await Promise.all(saveTasks);
    }
  }, [
    filesToSave,
    companySelectedMeasurementDate,
    selectedCaptable,
    notes,
    notesHasChanged,
    spreadsheetNotes,
    spreadsheetNoteHasChanged,
    addReferenceForExistingDocuments,
    saveNotes,
    saveSpreadsheetNotes
  ]);

  // ============================================================
  // Saved Views
  // ============================================================
  const [saveViewDialogOpen, setSaveViewDialogOpen] = useState(false);

  const savedViewsCompanyId = isFirmPage
    ? (urlStateWithData.companyIdFromUrl ?? null)
    : (companyIdFromRoute ?? null);

  const savedViewsMeasurementDateId = urlStateWithData.selectedMeasurementDate?.cmd_id ?? null;
  const { data: savedViewsData } = useWaterfallViews(savedViewsCompanyId, savedViewsMeasurementDateId);
  const savedViews = useMemo(() => savedViewsData ?? [], [savedViewsData]);
  const { mutateAsync: createWaterfallView, isPending: isCreatingView } = useCreateWaterfallView();
  const { mutateAsync: deleteWaterfallView, isPending: isDeletingView } = useDeleteWaterfallView();
  const { mutateAsync: updateWaterfallView, isPending: isAutoSaving } = useUpdateWaterfallView();
  const [viewMenuState, setViewMenuState] = useState<{ anchorEl: HTMLButtonElement; viewId: number } | null>(null);
  const [deleteConfirmViewId, setDeleteConfirmViewId] = useState<number | null>(null);
  // Tracks which saved view the user explicitly navigated to; stays set even when URL params diverge
  // from the saved view's stored state, preventing the tab from auto-reverting to "Current".
  const [selectedViewId, setSelectedViewId] = useState<number | null>(null);

  const handleSaveView = useCallback(
    async (name: string) => {
      if (savedViewsCompanyId == null) return;
      const newView = await createWaterfallView({
        company: savedViewsCompanyId,
        name,
        measurement_date: urlStateWithData.selectedMeasurementDate?.cmd_id ?? null,
        cap_table: urlStateWithData.captableIdFromUrl ?? null,
        exit_date: urlStateWithData.exitDateFromUrl
          ? moment(urlStateWithData.exitDateFromUrl, 'MM/DD/YYYY').format('YYYY-MM-DD')
          : null,
        exit_equity_value: urlStateWithData.equityValueFromUrl != null
          ? String(urlStateWithData.equityValueFromUrl)
          : null,
        exit_enterprise_value: urlStateWithData.enterpriseValueFromUrl != null
          ? String(urlStateWithData.enterpriseValueFromUrl)
          : null,
        plus_cash: urlStateWithData.cashOverrideFromUrl != null
          ? String(urlStateWithData.cashOverrideFromUrl)
          : null,
        less_debt: urlStateWithData.debtOverrideFromUrl != null
          ? String(urlStateWithData.debtOverrideFromUrl)
          : null,
      });
      setSelectedViewId(newView.id);
      setSaveViewDialogOpen(false);
    },
    [savedViewsCompanyId, createWaterfallView, urlStateWithData]
  );

  const handleSelectCurrentView = useCallback(() => {
    resetUrlDefaults();
    setSelectedViewId(null);
  }, [resetUrlDefaults]);

  const handleItemMenuClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>, item: { value: string }) => {
      const viewId = parseInt(item.value, 10);
      if (!isNaN(viewId)) {
        setViewMenuState({ anchorEl: event.currentTarget, viewId });
      }
    },
    []
  );

  const handleCloseViewMenu = useCallback(() => setViewMenuState(null), []);

  const handleRequestDeleteView = useCallback(() => {
    if (viewMenuState == null) return;
    setDeleteConfirmViewId(viewMenuState.viewId);
    setViewMenuState(null);
  }, [viewMenuState]);

  const handleConfirmDeleteView = useCallback(async () => {
    if (deleteConfirmViewId == null) return;
    await deleteWaterfallView(deleteConfirmViewId);
    setDeleteConfirmViewId(null);
  }, [deleteConfirmViewId, deleteWaterfallView]);

  const handleCancelDeleteView = useCallback(() => setDeleteConfirmViewId(null), []);

  const deleteConfirmViewName = useMemo(
    () => savedViews.find(v => v.id === deleteConfirmViewId)?.name,
    [savedViews, deleteConfirmViewId]
  );

  const { items: viewNavItems, activeViewId: urlMatchedViewId } = useWaterfallViewNavItems({
    views: savedViews,
    urlState: urlStateWithData,
    isFirmPage: isFirmPage ?? false,
    firmSlug,
    companySlug,
    companyList,
    onSelectDefault: handleSelectCurrentView,
    onSelectView: setSelectedViewId,
  });

  // Reset when company or measurement date changes so stale view IDs don't carry over.
  useEffect(() => {
    setSelectedViewId(null);
  }, [savedViewsCompanyId, savedViewsMeasurementDateId]);

  // Reset if the selected view was deleted.
  useEffect(() => {
    if (selectedViewId != null && !savedViews.some(v => v.id === selectedViewId)) {
      setSelectedViewId(null);
    }
  }, [savedViews, selectedViewId]);

  // Auto-save: when the URL diverges from the selected saved view, debounce-PATCH it.
  // Uses individual primitive deps (not the full urlStateWithData object) so the effect
  // only fires when a relevant value actually changes.
  const autoSaveMeasurementDate = urlStateWithData.selectedMeasurementDate?.cmd_id ?? null;
  const autoSaveCaptable = urlStateWithData.captableIdFromUrl ?? null;
  const autoSaveExitDate = urlStateWithData.exitDateFromUrl ?? null;
  const autoSaveEquityValue = urlStateWithData.equityValueFromUrl ?? null;
  const autoSaveEnterpriseValue = urlStateWithData.enterpriseValueFromUrl ?? null;
  const autoSavePlusCash = urlStateWithData.cashOverrideFromUrl ?? null;
  const autoSaveLessDebt = urlStateWithData.debtOverrideFromUrl ?? null;

  useEffect(() => {
    if (selectedViewId == null || urlMatchedViewId === selectedViewId) return;

    const timerId = setTimeout(() => {
      updateWaterfallView({
        id: selectedViewId,
        payload: {
          measurement_date: autoSaveMeasurementDate,
          cap_table: autoSaveCaptable,
          exit_date: autoSaveExitDate
            ? moment(autoSaveExitDate, 'MM/DD/YYYY').format('YYYY-MM-DD')
            : null,
          exit_equity_value: autoSaveEquityValue != null ? String(autoSaveEquityValue) : null,
          exit_enterprise_value: autoSaveEnterpriseValue != null ? String(autoSaveEnterpriseValue) : null,
          plus_cash: autoSavePlusCash != null ? String(autoSavePlusCash) : null,
          less_debt: autoSaveLessDebt != null ? String(autoSaveLessDebt) : null,
        },
      });
    }, 400);

    return () => clearTimeout(timerId);
  }, [
    selectedViewId,
    urlMatchedViewId,
    autoSaveMeasurementDate,
    autoSaveCaptable,
    autoSaveExitDate,
    autoSaveEquityValue,
    autoSaveEnterpriseValue,
    autoSavePlusCash,
    autoSaveLessDebt,
    updateWaterfallView,
  ]);

  const viewNavActiveValue = selectedViewId != null ? String(selectedViewId) : DEFAULT_VIEW_VALUE;
  const isViewDirty = selectedViewId != null && urlMatchedViewId !== selectedViewId;

  const tertiaryNavigation = useMemo(
    () => ({
      items: viewNavItems,
      value: viewNavActiveValue,
      onItemMenuClick: handleItemMenuClick,
    }),
    [viewNavItems, viewNavActiveValue, handleItemMenuClick]
  );

  useTertiaryNavigationRegistration(tertiaryNavigation);

  // ============================================================
  // Page Actions
  // ============================================================
  const hasPendingSaveChanges = !isEmpty(filesToSave)
    || (!isEmpty(notes) && notesHasChanged)
    || (!isEmpty(spreadsheetNotes) && spreadsheetNoteHasChanged);

  const saveNavContribution = useMemo(() => ({
    actions: [
      {
        type: 'button' as const,
        key: 'waterfall-save-view',
        label: 'Save View',
        savingLabel: 'Saving...',
        savedLabel: 'Saved',
        isSaving: isAutoSaving || isViewDirty,
        variant: 'secondary' as const,
        disabled: savedViewsCompanyId == null,
        onClick: () => setSaveViewDialogOpen(true),
      },
      {
        type: 'button' as const,
        key: 'waterfall-save',
        label: 'Save Notes & Documents',
        variant: 'positive' as const,
        disabled: !hasPendingSaveChanges,
        onClick: saveDrawer,
      },
    ],
  }), [hasPendingSaveChanges, saveDrawer, savedViewsCompanyId, isAutoSaving, isViewDirty]);

  useSecondaryNavigationContributionRegistration(saveNavContribution);

  // ============================================================
  // Children Companies
  // ============================================================
  const childrenCompanies = useMemo(() => {
    if (child_companies) {
      return child_companies;
    }
    return (companyList ?? []).find(c => c.company_id === selectedCompanyId)?.child_companies ?? null;
  }, [child_companies, companyList, selectedCompanyId]);

  // ============================================================
  // Valid Securities (needed for distribution hook)
  // ============================================================
  const validSecurities = useMemo(() => {
    if (captableDetail) {
      return getValidSecurities(captableDetail);
    }
    return [];
  }, [captableDetail]);

  // ============================================================
  // Waterfall Distribution
  // ============================================================
  const { scenarios, setScenarios, updatedWeightedShareValues } = useWaterfallDistribution({
    selectedCaptable,
    equityValue: calculatedEquityValue,
    exitDate,
    captableDetail,
    capTableList,
    isInitializedFromUrl: isInitialized,
    isInitializingFromUrl: !isInitialized,
    selectedCompanyId,
    fundList,
    childrenCompanies,
    childrenCapTables,
    validSecurities,
  });

  // ============================================================
  // Portfolio/Related Entity Logic
  // ============================================================
  const isSelectedPortfolioPrimaryCapTable = useMemo(() => {
    if (!selectedCaptable || isEmpty(capTableList)) {
      return false;
    }
    return Boolean(capTableList.some(captable => captable.id === selectedCaptable && captable.is_primary));
  }, [selectedCaptable, capTableList]);

  const shouldRunRelatedEntitiesWaterfall = useMemo(
    () => isSelectedPortfolioPrimaryCapTable && (childrenCompanies ?? []).length > 0,
    [isSelectedPortfolioPrimaryCapTable, childrenCompanies]
  );

  const { generateChildrenCompaniesCapTablesVersions } = useGenerateChildrenCompaniesCapTablesVersions(
    companyList,
    selectedMeasurementDateFromRoute?.date ?? companySelectedMeasurementDate?.date,
    selectedCompanyId ? Number(selectedCompanyId) : undefined
  );

  const selectedCompanyInformation = useMemo(() => {
    if (isSelectedPortfolioPrimaryCapTable) {
      return (companyList ?? []).find(ct => ct.company_id === selectedCompanyId);
    }
    const childCompanyId = childrenCapTables.find(ct => ct.id === selectedCaptable);
    return (childrenCompanies ?? []).find(child => Number(child.company_id) === Number(childCompanyId?.company_id));
  }, [
    companyList,
    selectedCompanyId,
    isSelectedPortfolioPrimaryCapTable,
    childrenCapTables,
    childrenCompanies,
    selectedCaptable,
  ]);
  const selectedCompanySlug = selectedCompanyInformation?.company_slug ?? (!isFirmPage ? companyInfo?.slug : undefined);
  const incompleteFundOwnershipUrl = useMemo(() => {
    if (!firmInfo?.slug || !selectedCompanySlug) {
      return null;
    }

    return fundOwnershipUrl(firmInfo.slug, selectedCompanySlug);
  }, [firmInfo?.slug, selectedCompanySlug]);

  useEffect(() => {
    const fetchAndSetChildrenCapTables = async () => {
      const result = await generateChildrenCompaniesCapTablesVersions();
      const allChildrenCapTables = Object.values(result).flat();
      setChildrenCapTables(allChildrenCapTables.filter(ct => ct.is_primary));
    };
    fetchAndSetChildrenCapTables();
  }, [generateChildrenCompaniesCapTablesVersions]);

  // ============================================================
  // Sync cap table URL when cap table list changes
  // If the current URL cap table is not in the new list, default to primary
  // ============================================================
  useEffect(() => {
    // Skip if cap table list is still loading or empty
    if (waterfallData.isCapTableListLoading || !capTableList.length) {
      return;
    }

    const currentCaptableId = urlStateWithData.captableIdFromUrl;

    // If URL has a cap table, check if it's in the current list (including children)
    if (currentCaptableId !== undefined) {
      const isInMainList = capTableList.some(ct => ct.id === currentCaptableId);
      const isInChildrenList = childrenCapTables.some(ct => ct.id === currentCaptableId);
      if (isInMainList || isInChildrenList) {
        return;
      } // Cap table is valid, nothing to do
    }

    // URL cap table is not in the list (or URL has no cap table) - set to primary
    const primaryCaptable = capTableList.find(ct => ct.is_primary);
    if (primaryCaptable) {
      urlStateWithData.setCaptable(primaryCaptable);
    }
  }, [capTableList, childrenCapTables, waterfallData.isCapTableListLoading, urlStateWithData.captableIdFromUrl, urlStateWithData.setCaptable, urlStateWithData]);

  // ============================================================
  // Format & Exchange Rate
  // ============================================================
  const {
    format: fundFormat,
    formatDispatch: fundFormatDispatch,
    fundExchangeRateProvider,
    fundExchangeRateSheet,
    isAdditionalCurrencySelected
  } = useFundExchangeRate({
    page: CAP_TABLE_CURRENCY_PAGE,
    units: REGULAR_UNIT,
    measurementDate: companySelectedMeasurementDate,
    marketDate: exitDate ?? null,
    captable: captableDetail,
  });

  const [format, formatDispatch] = useFormat({
    page: CAP_TABLE_CURRENCY_PAGE,
    units: REGULAR_UNIT,
    measurementDate: companySelectedMeasurementDate,
  }) as unknown as UseFormatValues;

  // ============================================================
  // Company Info Fetch
  // ============================================================
  useEffect(() => {
    if (selectedCompanyId && (isEmpty(companyInfo) || selectedCompanyId !== companyInfo?.id)) {
      getCompanyInfoById(selectedCompanyId);
    }
  }, [selectedCompanyId, companyInfo, getCompanyInfoById]);

  // ============================================================
  // Main Cap Table Ref
  // ============================================================
  useEffect(() => {
    if (capTableList && capTableList.length > 0) {
      const primaryCapTable = capTableList.find(captable => captable.is_primary);
      mainCapTableRef.current = primaryCapTable?.id ?? null;
    }
  }, [capTableList]);

  // Update main fund ownership ref when captable detail changes
  useEffect(() => {
    if (captableDetail && mainCapTableRef.current === selectedCaptable) {
      mainFundOwnershipRef.current = captableDetail.fund_ownership || null;
    }
  }, [captableDetail, selectedCaptable]);

  // ============================================================
  // Firm Input Data
  // ============================================================
  const firmInputData = useMemo(() => {
    if (isFirmPage) {
      return {
        companyId: selectedCompanyId,
        measurementDateId: companySelectedMeasurementDate?.id,
      };
    }
  }, [isFirmPage, selectedCompanyId, companySelectedMeasurementDate]);

  // ============================================================
  // Sheet Configurations
  // ============================================================
  const { sheetConfig: waterfallSheet } = useWaterfallSheetConfig({
    scenarioObject: scenarios,
    validSecurities,
    companyName: selectedCompanyInformation?.company_name ?? '',
  });

  const { spreadsheets: fundInvestmentSheets } = useFundInvestmentSheetConfig({
    weightedShareValues: updatedWeightedShareValues,
    validSecurities,
    idToAssociateTo: null,
  });

  const {
    ownersShareValuesMap,
    relatedEntitiesWaterfalls,
    childrenSheets,
    registerChildrenSheets,
    resetChildrenSheets,
  } = useRelatedEntitiesWaterfalls({
    updatedWeightedShareValues,
    childrenCapTables,
    childrenCompanies: childrenCompanies ?? [],
    exitDate: exitDate ?? '',
    fundList: fundList ?? [],
    mainFundOwnershipRef,
  });

  const { sheetConfig: summarySheetConfig } = useWaterfallFundSummarySheetConfig({
    captable: captableDetail,
    fundList,
    childrenCompanies,
    calculatedEquityValue,
    fundIrrMap,
  });

  const { spreadsheet: firmTotalSheet } = useFirmTotalSheetConfig({
    weightedShareValues: updatedWeightedShareValues,
    securityCount: validSecurities.length,
    investmentsInPortfolioSheets: fundInvestmentSheets,
    allInvestmentsSheets: [...fundInvestmentSheets, ...Object.values(childrenSheets).flat()],
    isSelectedPortfolioPrimaryCapTable,
    fundInvestmentsSheetsNamesRef,
    waterfallHasRunAtLeastOnce: Boolean(scenarios),
  });

  const inputSheet = useCreateInputSheet({
    exitDate,
    // Pass as string to match dropdown option values
    capTableId: selectedCaptable !== undefined ? String(selectedCaptable) : undefined,
    equityValue,
    enterpriseValue,
    firmOptions: firmInputData,
    childrenCapTables,
    parentCapTables: capTableList,
    fundInvestmentsSheetsNamesRef,
    plusCashIsFromFinancials: cashIsFromFinancials,
    lessDebtIsFromFinancials: debtIsFromFinancials,
    plusCash: effectiveCash,
    lessDebt: effectiveDebt,
    isAdditionalCurrencySelected
  });

  const sheets = useMemo(() => {
    if (shouldRunRelatedEntitiesWaterfall && Object.values(childrenSheets).flat().length > 0) {
      return [
        inputSheet,
        fundExchangeRateSheet,
        waterfallSheet,
        ...fundInvestmentSheets,
        ...Object.values(childrenSheets).flat(),
        firmTotalSheet,
        summarySheetConfig,
      ].filter(sheet => !!sheet);
    }
    return [
      inputSheet,
      fundExchangeRateSheet,
      waterfallSheet,
      ...fundInvestmentSheets,
      firmTotalSheet,
      summarySheetConfig,
    ].filter(sheet => !!sheet);
  }, [
    fundExchangeRateSheet,
    inputSheet,
    waterfallSheet,
    fundInvestmentSheets,
    childrenSheets,
    firmTotalSheet,
    shouldRunRelatedEntitiesWaterfall,
    summarySheetConfig,
  ]);

  const { cells: workbookCells, data: workbookData, onChange, workbookContextValue } = useWorkbook(sheets);

  useFundExchangeRateSheet({ cells: workbookCells, onChange, exchangeRate: fundExchangeRateProvider.exchangeRate });

  // ============================================================
  // Sync URL state to spreadsheet cells
  // When URL changes (e.g., via defaults), update the corresponding cells
  // ============================================================
  useEffect(() => {
    if (!workbookCells?.InputSheet) {
      return;
    }

    // Sync cap table value from URL to cell (pass as string to match dropdown option values)
    const capTableCell = Object.values(workbookCells.InputSheet).find(c => c.alias === CAP_TABLE_ALIAS);
    if (capTableCell && selectedCaptable !== undefined) {
      const currentValue = capTableCell.value;
      const selectedCaptableStr = String(selectedCaptable);
      // Only update if the value is different (avoid infinite loops)
      if (String(currentValue) !== selectedCaptableStr) {
        onChange(capTableCell, selectedCaptableStr);
      }
    }

    // Sync measurement date value from URL to cell (for firm pages, pass as string)
    if (isFirmPage) {
      const measurementDateCell = Object.values(workbookCells.InputSheet).find(c => c.alias === MEASUREMENT_DATE_ALIAS);
      const resolvedMeasurementDateId = urlStateWithData.selectedMeasurementDate?.id;
      if (measurementDateCell && resolvedMeasurementDateId !== undefined) {
        const currentValue = measurementDateCell.value;
        const measurementDateIdStr = String(resolvedMeasurementDateId);
        if (String(currentValue) !== measurementDateIdStr) {
          onChange(measurementDateCell, measurementDateIdStr);
        }
      }
    }
  }, [workbookCells, selectedCaptable, urlStateWithData.selectedMeasurementDate?.id, isFirmPage, onChange]);

  // ============================================================
  // IRR Data
  // ============================================================
  const { isInputsLoaded } = useWaterfallIrr({
    workbookCells,
    selectedCaptable,
    exitDate,
    onChange,
    setFundIrrMap,
    isFirmPage: isFirmPage ?? false,
    companyList,
    isFinancialsLoading: waterfallData.isFinancialsLoading,
  });

  // ============================================================
  // Input Handlers
  // ============================================================
  const { onWorkbookChange } = useWaterfallInputHandlers({
    urlState: urlStateWithData,
    onChange,
    workbookCells,
    setScenarios,
    resetChildrenSheets,
    capTableList,
    childrenCapTables,
    companyMeasurementDates,
  });

  // ============================================================
  // Loading State
  // ============================================================
  useEffect(() => {
    if (isFirmPage) {
      setIsLoading(!companyList?.length);
    }
  }, [companyList, isFirmPage]);

  const combinedIsLoading = isLoading || waterfallData.isLoading || isInitializing;

  // ============================================================
  // Context Values
  // ============================================================
  const waterfallContextValues = useMemo(
    () => ({
      companyList: companyList ?? [],
      measurementDates: companyMeasurementDates,
      capTableList,
      workbookData,
      onChange,
      format,
      formatDispatch,
    }),
    [companyList, companyMeasurementDates, capTableList, workbookData, onChange, format, formatDispatch]
  );

  // ============================================================
  // Render
  // ============================================================
  return (
    <>
      <SaveWaterfallViewDialog
        open={saveViewDialogOpen}
        isSaving={isCreatingView}
        onSave={handleSaveView}
        onClose={() => setSaveViewDialogOpen(false)}
      />
      <Menu
        anchorEl={viewMenuState?.anchorEl}
        open={viewMenuState != null}
        onClose={handleCloseViewMenu}
      >
        <MenuItem onClick={handleRequestDeleteView}>Delete</MenuItem>
      </Menu>
      <ConfirmationDialog
        open={deleteConfirmViewId != null}
        title="Delete Saved View"
        isLoading={isDeletingView}
        onClose={handleCancelDeleteView}
        actions={[
          { label: 'Cancel', variant: 'outlined' },
          {
            label: 'Delete',
            type: 'danger',
            variant: 'contained',
            callback: () => {
              handleConfirmDeleteView();
            },
          },
        ]}
      >
        <ConfirmationDelete
          customMessage={`Are you sure you want to delete "${deleteConfirmViewName ?? 'this view'}"?`}
        />
      </ConfirmationDialog>
      <WorkbookContext.Provider value={workbookContextValue}>
        <FundExchangeRateContext.Provider value={fundExchangeRateProvider}>
          <WaterfallContext.Provider value={waterfallContextValues}>
            <CustomContainer className={WATERFALL_CONTAINER_CLASS}>
              <CustomContainer>
                {workbookData && inputSheet?.data && isInputsLoaded && (
                  <>
                    <Grid className={WATERFALL_INPUT_CONTAINER_CLASS} id="waterfall-inputs" size={{ xs: 12 }}>
                      <ScalarSpreadsheet
                        {...inputSheet}
                        format={fundFormat}
                        showToolbar
                        formatDispatch={fundFormatDispatch}
                        onChange={onWorkbookChange}
                        disableUnsavedChanges
                        disableVerticalScroll
                        sheet={inputSheet}
                      />
                    </Grid>

                    {summarySheetConfig && (
                      <Grid className={WATERFALL_SUMMARY_CONTAINER_CLASS} size={{ xs: 12 }}>
                        <ScalarSpreadsheet
                          {...summarySheetConfig}
                          format={format}
                          showToolbar
                          formatDispatch={formatDispatch}
                          onChange={onWorkbookChange}
                          disableUnsavedChanges
                          disableVerticalScroll
                          sheet={summarySheetConfig}
                        />{' '}
                      </Grid>
                    )}
                  </>
                )}
              </CustomContainer>

              {waterfallSheet && workbookData && updatedWeightedShareValues && (
                <CustomContainer>
                  <CustomCompactContainer>
                    <CustomContainer>
                      <Grid size={{ xs: 12 }}>
                        <WaterfallTables
                          waterfallSheet={waterfallSheet as ScalarSpreadsheetConfigDataInterface}
                          fundInvestmentsSheets={fundInvestmentSheets}
                          firmTotalSheet={
                            Object.entries(ownersShareValuesMap ?? {}).flat().length ? undefined : firmTotalSheet
                          }
                        />
                      </Grid>

                      {shouldRunRelatedEntitiesWaterfall &&
                        ownersShareValuesMap &&
                        Object.entries(ownersShareValuesMap).map(([relatedEntityId, sharesValue]) => (
                          <Grid className={WATERFALL_RELATED_ENTITY_CONTAINER_CLASS} key={relatedEntityId}>
                            <RelatedEntityWaterfallSheets
                              relatedEntity={
                                (childrenCompanies ?? []).find(
                                  child => Number(child.company_id) === Number(relatedEntityId)
                                ) ?? ({} as ChildCompany)
                              }
                              scenarioResult={relatedEntitiesWaterfalls?.[Number(relatedEntityId)] as Scenario}
                              childrenCapTables={childrenCapTables}
                              ownedSharesValue={sharesValue}
                              registerChildrenSheets={registerChildrenSheets}
                            />
                          </Grid>
                        ))}
                    </CustomContainer>

                    {exitDate &&
                      (equityValue || calculatedEquityValue) &&
                      companySelectedMeasurementDate &&
                      selectedCompanyInformation &&
                      selectedCaptable ? (
                        <DownloadWaterfall
                          exitDate={exitDate}
                          equityValue={equityValue ?? (calculatedEquityValue as number)}
                          companyMeasurementDate={companySelectedMeasurementDate}
                          selectedCapTableId={selectedCaptable}
                          selectedCompanyId={Number(selectedCompanyInformation.company_id)}
                        />
                      ) : null}
                  </CustomCompactContainer>
                </CustomContainer>
              )}

              <LoadingIndicator isLoading={combinedIsLoading} />

              {validSecurities?.length === 0 && scenarios !== undefined && selectedCaptable && (
                <EmptyTableMessage
                  title="The selected company has an incomplete cap table"
                  tableTerms={{
                    tableName: 'Cap table',
                    pluralColumnName: 'Securities',
                    columnName: 'Security',
                  }}
                />
              )}

              {updatedWeightedShareValues && isEmpty(updatedWeightedShareValues) && (
                <NeedsFundOwnershipFirst
                  fundOwnershipURL={incompleteFundOwnershipUrl}
                  measurementDates={companyMeasurementDates}
                  selectedMeasurementDate={companySelectedMeasurementDate}
                  title="No fund ownership defined for the cap table"
                  tagline="Add fund ownership to see its share values"
                />
              )}
              <MDDialogue />
            </CustomContainer>
          </WaterfallContext.Provider>
        </FundExchangeRateContext.Provider>
      </WorkbookContext.Provider>
      {companySelectedMeasurementDate && selectedCaptable && (
        <WidgetDrawer
          applicationContext={WidgetDrawerApplicationContext.CAP_TABLE}
          notesProps={{
            pageType: SpreadsheetNote.page_type.WTRFL,
            pageTypeKey: WATERFALL_PAGE_KEY,
            pageTypeId: companySelectedMeasurementDate.cmd_id,
            notes,
            setNotes,
            onAddNote,
            onUpdateNotes,
            onDeleteNote,
            isDisabled: false,
          }}
          documentReferencesProps={{
            selectedMeasurementDate: companySelectedMeasurementDate,
            referenceType: CAP_TABLE_REFERENCE_TYPE,
            currentPage: CAP_TABLE_REFERENCE_TITLE,
            referencedFeatureId: selectedCaptable,
            filesToSave,
            setFilesToSave,
            isDisplayingRowNumbers: false,
            isDisabled: false,
          }}
          spreadsheetNotesProps={{
            pageTypeId: companySelectedMeasurementDate.cmd_id as number,
            pageType: SpreadsheetNote.page_type.WTRFL,
            pageTypeKey: WATERFALL_PAGE_KEY,
            notes: spreadsheetNotes,
            setNotes: setSpreadsheetNotes,
            saveNotes: () => {
              saveSpreadsheetNotes();
            },
            handleOnDeleteNote: handleOnDeleteSpreadsheetNote,
          }}
        />
      )}
    </>
  );
};

export default WaterfallDisplay;
